import { ChangeDetectionStrategy, Component, DestroyRef, effect, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { IconComponent } from '@examdojo/core/icon';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import { THEME_COLORS_WITH_SHADES } from '@examdojo/core/widget';
import type {
  WidgetControl,
  WidgetSliderControl,
  WidgetSliderVariant,
  WidgetButtonControl,
} from '@examdojo/core/widget';
import { WIDGET_CONTROL_TYPE } from '@examdojo/core/widget';

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'dojo-behavior-section',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatSlideToggleModule,
    IconButtonComponent,
    IconComponent,
    NumberInputComponent,
    TextInputComponent,
    SingleSelectComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './behavior-section.component.html',
})
export class BehaviorSectionComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    effect(() => {
      const controls = this.controls();
      this.syncControlsToForm(controls);
    });
  }

  readonly controls = input<WidgetControl[]>([]);
  readonly elementIds = input<string[]>([]);

  readonly controlsChange = output<WidgetControl[]>();

  readonly newControlTypeCtrl = new FormControl<string>('', { nonNullable: true });

  private readonly textCtrls = new Map<string, FormControl<string>>();
  private readonly numberCtrls = new Map<string, FormControl<number | null>>();
  private readonly selectCtrls = new Map<string, FormControl<string>>();

  readonly controlTypeOptions: SelectOption[] = [
    { label: 'Slider', value: 'slider' },
    { label: 'Button', value: 'button' },
  ];

  readonly positionOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    { label: 'Top Left', value: 'top-left' },
    { label: 'Top', value: 'top' },
    { label: 'Top Right', value: 'top-right' },
    { label: 'Bottom Left', value: 'bottom-left' },
    { label: 'Bottom', value: 'bottom' },
    { label: 'Bottom Right', value: 'bottom-right' },
    { label: 'Slider Footer', value: 'slider-footer' },
  ];

  readonly colorOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    ...THEME_COLORS_WITH_SHADES.map((c) => ({ label: c, value: c })),
  ];

  readonly variantOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    { label: 'Minimal', value: 'minimal' },
  ];

  getTextCtrl(idx: number, field: string): FormControl<string> {
    const key = `${idx}-${field}`;
    if (!this.textCtrls.has(key)) {
      const ctrl = new FormControl<string>('', { nonNullable: true });
      this.textCtrls.set(key, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((val) => {
            this.onControlFieldChange(idx, field, val);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.textCtrls.get(key)!;
  }

  getNumberCtrl(idx: number, field: string): FormControl<number | null> {
    const key = `${idx}-${field}`;
    if (!this.numberCtrls.has(key)) {
      const ctrl = new FormControl<number | null>(null);
      this.numberCtrls.set(key, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((val) => {
            this.onControlFieldChange(idx, field, val);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.numberCtrls.get(key)!;
  }

  getSelectCtrl(idx: number, field: string): FormControl<string> {
    const key = `${idx}-${field}`;
    if (!this.selectCtrls.has(key)) {
      this.selectCtrls.set(key, new FormControl<string>('', { nonNullable: true }));
    }
    return this.selectCtrls.get(key)!;
  }

  isSliderEditable(control: WidgetControl): boolean {
    return control.type === WIDGET_CONTROL_TYPE.Slider && (control as WidgetSliderControl).editable === true;
  }

  isButtonShowLabel(control: WidgetControl): boolean {
    return control.type === WIDGET_CONTROL_TYPE.Button && (control as WidgetButtonControl).showLabel !== false;
  }

  onControlFieldChange(idx: number, field: string, value: unknown): void {
    const controls = [...this.controls()];
    if (idx >= controls.length) {
      return;
    }

    const control = { ...controls[idx] } as Record<string, unknown>;

    if (field === 'rangeMin' || field === 'rangeMax' || field === 'rangeStep') {
      const slider = control as unknown as WidgetSliderControl;
      const range: [number, number, number?] = [...(slider.range ?? [0, 10])];
      if (field === 'rangeMin') {
        range[0] = (value as number) ?? 0;
      }
      if (field === 'rangeMax') {
        range[1] = (value as number) ?? 10;
      }
      if (field === 'rangeStep') {
        range[2] = (value as number) ?? undefined;
      }
      control['range'] = range;
    } else if (field === 'position') {
      control[field] = (value as string) || undefined;
    } else if (field === 'color') {
      control[field] = (value as string) || undefined;
    } else if (field === 'variant') {
      const variant = ((value as string) || undefined) as WidgetSliderVariant | undefined;
      control[field] = variant;
      // Apply variant to all slider controls for consistency
      for (let i = 0; i < controls.length; i++) {
        if (i !== idx && controls[i].type === WIDGET_CONTROL_TYPE.Slider) {
          controls[i] = { ...controls[i], variant } as unknown as WidgetControl;
        }
      }
    } else {
      control[field] = value;
    }

    controls[idx] = control as unknown as WidgetControl;
    this.controlsChange.emit(controls);
  }

  onDeleteControl(idx: number): void {
    const controls = [...this.controls()];
    controls.splice(idx, 1);
    this.controlsChange.emit(controls);
  }

  onAddControl(): void {
    const typeValue = this.newControlTypeCtrl.value;
    if (!typeValue) {
      return;
    }

    const controls = [...this.controls()];
    const id = `ctrl-${controls.length + 1}`;

    if (typeValue === 'slider') {
      controls.push({
        type: WIDGET_CONTROL_TYPE.Slider,
        id,
        label: 'New Slider',
        param: '',
        range: [-10, 10, 0.1],
      } as WidgetSliderControl);
    } else if (typeValue === 'button') {
      controls.push({
        type: WIDGET_CONTROL_TYPE.Button,
        id,
        label: 'New Button',
        trigger: { action: 'TRANSFORM_VIEW' },
      } as WidgetButtonControl);
    }

    this.controlsChange.emit(controls);
    this.newControlTypeCtrl.setValue('', { emitEvent: false });
  }

  private syncControlsToForm(controls: WidgetControl[]): void {
    for (let idx = 0; idx < controls.length; idx++) {
      const control = controls[idx];

      this.getTextCtrl(idx, 'label').setValue(control.label ?? '', { emitEvent: false });
      this.getTextCtrl(idx, 'id').setValue(control.id ?? '', { emitEvent: false });
      this.getSelectCtrl(idx, 'position').setValue((control.position as string) ?? '', { emitEvent: false });

      if (control.type === WIDGET_CONTROL_TYPE.Slider) {
        const slider = control as WidgetSliderControl;
        this.getTextCtrl(idx, 'param').setValue(slider.param ?? '', { emitEvent: false });
        this.getNumberCtrl(idx, 'rangeMin').setValue(slider.range?.[0] ?? 0, { emitEvent: false });
        this.getNumberCtrl(idx, 'rangeMax').setValue(slider.range?.[1] ?? 10, { emitEvent: false });
        this.getNumberCtrl(idx, 'rangeStep').setValue(slider.range?.[2] ?? null, { emitEvent: false });
        this.getNumberCtrl(idx, 'defaultValue').setValue(slider.defaultValue ?? null, { emitEvent: false });
        this.getSelectCtrl(idx, 'color').setValue((slider.color as string) ?? '', { emitEvent: false });
        this.getSelectCtrl(idx, 'variant').setValue((slider.variant as string) ?? '', { emitEvent: false });
      } else if (control.type === WIDGET_CONTROL_TYPE.Button) {
        const button = control as WidgetButtonControl;
        this.getTextCtrl(idx, 'icon').setValue(button.icon ?? '', { emitEvent: false });
      }
    }
  }
}
